import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";

const NAV_LINKS = [
    { to: "/", label: "Home", id: "home" },
    { to: "/about", label: "About", id: "about" },
    { to: "/expertise", label: "Expertise", id: "expertise" },
    { to: "/experience", label: "Experience", id: "experience" },
    { to: "/approach", label: "Approach", id: "approach" },
    { to: "/contact", label: "Contact", id: "contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header className="fixed inset-x-0 top-0 z-50" data-testid="navbar">
            {/* Main bar */}
            <div className="border-b border-[#243650] bg-[#0B1322]/92 backdrop-blur-xl">
                <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-6 lg:px-10">
                    <Link to="/" className="flex items-center gap-3.5" data-testid="nav-logo">
                        <span className="inline-flex items-center bg-[#F2EEE4] px-2 py-1.5 shadow-[3px_3px_0_rgba(217,168,78,0.5)]">
                            <img src="/logo.png" alt="Infrasic Intelligence emblem" className="h-11 w-auto" />
                        </span>
                        <span className="hidden leading-none sm:block">
                            <span className="block font-sans text-[19px] font-semibold tracking-[0.12em] text-[#E8ECF4]">
                                INFRASIC
                            </span>
                            <span className="mt-1 block font-sans text-[9px] font-medium tracking-[0.34em] text-[#5B93E8]">
                                INTELLIGENCE
                            </span>
                        </span>
                    </Link>

                    <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-desktop">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-link-${link.id}`}
                                className={({ isActive }) =>
                                    `relative py-2 font-tech text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-[#A87B2F] after:transition-all after:duration-300 ${
                                        isActive
                                            ? "text-[#5B93E8] after:w-full"
                                            : "text-[#9AA7BF] after:w-0 hover:text-[#E8ECF4] hover:after:w-full"
                                    }`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                        <a
                            href="mailto:contactus@infrasic.in?subject=Discuss%20an%20Investigation"
                            data-testid="nav-cta-contact"
                            className="group ml-2 inline-flex items-center gap-2 bg-[#2E62B0] px-5 py-3 font-tech text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#3D74C6]"
                        >
                            DISCUSS AN INVESTIGATION
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </a>
                    </nav>

                    <button
                        className="text-[#E8ECF4] lg:hidden"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle navigation menu"
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>

                {open && (
                    <div className="border-t border-[#243650] bg-[#0F1B2E] px-6 py-8 lg:hidden" data-testid="nav-mobile-panel">
                        <nav className="flex flex-col gap-5">
                            {NAV_LINKS.map((link) => (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    data-testid={`nav-mobile-link-${link.id}`}
                                    className={({ isActive }) =>
                                        `font-tech text-xs uppercase tracking-[0.24em] ${
                                            isActive ? "text-[#5B93E8]" : "text-[#9AA7BF]"
                                        }`
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            ))}
                            <a
                                href="mailto:contactus@infrasic.in?subject=Discuss%20an%20Investigation"
                                data-testid="nav-mobile-cta-contact"
                                className="mt-2 inline-flex w-fit items-center gap-2 bg-[#2E62B0] px-5 py-3 font-tech text-xs font-semibold uppercase tracking-[0.2em] text-white"
                            >
                                DISCUSS AN INVESTIGATION
                                <ArrowUpRight className="h-3.5 w-3.5" />
                            </a>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
}
